<template>
  <div class="source-content legacy-graphics">
    <div class="filter-bar">
      <div class="tb-form-group">
        <i class="icon icon-magnifier"></i>

        <input-tag
          placeholder="Search"
          :tags="searchTerms"
          data-entity="producer-assets-searchSavedGraphicsInput"
          @add="addTag"
          @remove="removeTag"
        />

        <button
          class="tb-btn tb-btn-contained high-em margin-left-sm"
          data-entity="producer-assets-createSavedGraphic"
          @click="showTemplatePicker = true"
        >
          Create
        </button>
      </div>

      <div class="tb-form-group add-to-playlist-controls">
        <tgb-split-button
          :options="splitButtonOptions"
          :disabled="!selected.length"
          data-entity="producer-assets-addSelectedSavedGraphicsToPlaylist"
          @click="addSelectedToPlaylist('Graphics')"
        >
          Add to playlist
        </tgb-split-button>
      </div>

      <draggable
        v-if="searchResults.length"
        class="source-list"
        v-model="searchResults"
        :clone="clone"
        :sort="false"
        animation="200"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
        @start="startDrag"
        @end="$store.dispatch('clearDragType')"
        revert-on-spill="true"
      >
        <div
          v-for="(graphic, index) in searchResults"
          :key="index"
          class="source-item legacy-graphic"
          :data-template-id="graphic._id"
          data-entity="producer-assets-savedGraphic"
        >
          <div class="tb-checkbox">
            <input
              type="checkbox"
              :id="graphic._id"
              :value="graphic"
              v-model="selected"
              data-entity="producer-assets-toggleSavedGraphicSelection"
            >

            <label class="checkbox" :for="graphic._id" />
          </div>

          <div class="source-item-wrapper">
            <img
              class="preview margin-right-xs"
              v-if="lookupTable[graphic.template_id]"
              :src="lookupTable[graphic.template_id].preview_url"
            >

            <div class="source-title title-text" :title="graphic.title">
              {{graphic.title}}

              <br>

              <div
                v-if="lookupTable[graphic.template_id]"
                class="description-text capitalize"
              >
                {{lookupTable[graphic.template_id].title}}
              </div>
            </div>

            <div class="source-actions margin-left-xxs">
              <button
                class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                title="Add to playlist"
                data-entity="producer-assets-addSavedGraphicToPlaylist"
                @click="addToPlaylist(graphic, 'Saved Graphic')"
              >
                <span class="icon icon-plus" />
              </button>

              <dropdown position="left" class="more-actions margin-left-xxs" ref="dropdown">
                <template slot="trigger">
                  <button
                    class="tb-btn tb-btn-text sm medium-em"
                    title="More actions"
                    data-entity="producer-assets-toggleSavedGraphicContextMenu"
                  >
                    <span class="icon icon-more-vertical"></span>
                  </button>
                </template>

                <template slot="dropdown-body">
                  <div
                    class="dropdown-list-item"
                    data-entity="producer-assets-duplicateSavedGraphic"
                    @click="duplicateGraphic(graphic, index)"
                  >
                    <div class="dropdown-list-item-label">
                      Duplicate
                    </div>
                  </div>

                  <div
                    class="dropdown-list-item"
                    data-entity="producer-assets-deleteSavedGraphic"
                    @click="deleteConfirm(graphic)"
                  >
                    <div class="dropdown-list-item-label warning">
                      Delete
                    </div>
                  </div>
                </template>
              </dropdown>
            </div>
          </div>
        </div>
      </draggable>

      <template-modal
        v-if="showTemplatePicker"
        @close="showTemplatePicker = false"
      />

      <alert
        v-if="showAlert"
        footerConfirm="true"
        @confirm="deleteGraphic"
        @close="showAlert = false"
      >
        <h2>Are you sure?</h2>
        <p>This will delete this graphic and will break any production that is using it.</p>
      </alert>
    </div>
  </div>
</template>

<script>
import { filter, some, pick } from 'lodash-es';
import AlertComponent from '../../alert/Alert.vue';
import tabMixins from '../../../mixins/tab-mixins';
import TemplateModalComponent from '../../template-modal/TemplateModal.vue';
import SplitButton from '../../../../components/ui/split-button/TgbSplitButton.vue';

const { v1: uuidv1 } = require('uuid'); // Dis da new way

export default {
  mixins: [tabMixins],

  components: {
    alert: AlertComponent,
    'template-modal': TemplateModalComponent,
    'tgb-split-button': SplitButton,
  },

  data() {
    return {
      showAlert: false,
      showTemplatePicker: false,
      selectedGraphic: null,
    };
  },

  created() {
    this.$store.dispatch('getTemplates');

    this.$store.dispatch('getGraphics').then(() => {
      this.searchResults = this.graphics;
    });

    window.addEventListener('message', (e) => {
      if (e.origin !== process.env.TAGBOARD_GRAPHICS_URL) {
        return;
      }

      if (typeof e.data === 'string' && e.data.indexOf('tgbTemplateUpdate') === 0) {
        const temp = JSON.parse(e.data.split('|')[1]);
        this.$store.commit('EDIT_TEMPLATE', temp);
      }
    }, false);
  },

  beforeDestroy() {
    // window.removeEventListener('message', (e) => {}, false);
    this.$store.dispatch('setGraphics', []);
  },

  computed: {
    splitButtonOptions() {
      const groups = this.$store.state.production.production.timeline.filter((item) => (
        item.layout === 'graphics'
      ));

      const options = groups.map((group) => ({
        key: group.timeline_id,
        label: group.title,
        onClick: () => {
          this.addSelectedToGroup('Graphics', group.timeline_id);
        },
      }));

      if (options.length) {
        options.push({ divider: true });
      }

      // Option to create a new group and add to that instead
      options.push({
        key: 'new',
        label: '+ New Group',
        onClick: async () => {
          const group = await this.$store.dispatch('addGraphicsGroup', {
            posts: this.selected.map((item) => this.clone(item)),
          });

          this.selected = [];

          this.addSelectedToGroup('Graphics', group.timeline_id);
        },
      });

      return options;
    },

    graphics() {
      return this.$store.state.graphics.graphics;
    },

    owner() {
      return this.$store.state.production.production.owner;
    },

    graphicModalOpen() {
      return this.$store.state.graphics.graphicModalOpen;
    },

    assetModalOpen() {
      return this.$store.state.graphics.assetModalOpen;
    },

    lookupTable() {
      return this.$store.state.graphics.templatesLookupTable;
    },
  },

  methods: {
    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === parseInt(templateId, 10));
      this.$store.dispatch('setDragType', {
        type: 'graphic',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    doSearch() {
      if (!this.searchTerms.length) {
        this.searchResults = this.graphics;
        return;
      }

      this.searchResults = filter(this.graphics, (graphic) => {
        const tags = this.lookupTable[graphic.template_id]?.tags || [];
        const tagFound = some(this.searchTerms, (term) => (
          some(tags, (tag) => tag.toLowerCase().indexOf(term.toLowerCase()) !== -1)
        ));

        const titleFound = some(this.searchTerms, (term) => (
          graphic.title.toLowerCase().indexOf(term.toLowerCase()) !== -1
        ));

        return tagFound || titleFound;
      });
    },

    deleteConfirm(graphic) {
      this.selectedGraphic = graphic;
      this.showAlert = true;
    },

    duplicateGraphic(graphic, index) {
      this.$store.dispatch('duplicateGraphic', graphic);
      this.$refs.dropdown[index].hideDropdown();
    },

    async deleteGraphic() {
      this.$store.dispatch('startLoader');

      try {
        await this.$store.dispatch('deleteGraphic', this.selectedGraphic);
        this.successToast('Graphic deleted.');
      } catch (err) {
        this.errorToast('There was an error deleting this graphic. Please try again.');
      } finally {
        this.showAlert = false;
        this.selectedGraphic = null;
        this.$store.dispatch('stopLoader');
      }
    },

    clone(original) { // Called when dragging or adding to playlist
      const clone = pick(original, ['_id', 'template_id', 'title']);

      clone.timeline_id = uuidv1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.layout = 'graphic';
      clone.posts = [{ timeline_id: clone.timeline_id }];
      clone.fields = original.fields ? structuredClone(original.fields) : {};

      return clone;
    },
  },
};
</script>
